import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Tags, ShoppingCart, Users, FileText, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/admin/NotificationBell";

const LOGO = "/assets/kreyola-logo.webp";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/shipping", label: "Shipping", icon: Package },
  { to: "/admin/content", label: "Content", icon: FileText },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex-1 space-y-1">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`admin-nav-${item.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
              isActive ? "bg-gold text-navy" : "text-cream/70 hover:bg-white/5"
            }`
          }
        >
          <item.icon size={17} /> {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen bg-cream" data-testid="admin-page">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-navy p-4 lg:flex">
        <NavLink to="/" className="mb-6 flex items-center gap-2">
          <img src={LOGO} alt="" className="h-11 w-11 object-contain" />
          <div>
            <span className="block font-display text-lg font-bold leading-tight text-cream">Admin</span>
            <span className="block text-[11px] uppercase tracking-wide text-cream/40">Kreyola Boutik</span>
          </div>
        </NavLink>
        <NavItems />
        <button onClick={doLogout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sunset hover:bg-white/5">
          <LogOut size={17} /> Logout
        </button>
      </aside>

      {/* Mobile off-canvas drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <aside className="relative flex h-full w-72 flex-col bg-navy p-4 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LOGO} alt="" className="h-10 w-10 object-contain" />
                <span className="font-display text-lg font-bold text-cream">Admin</span>
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"><X className="text-cream" /></button>
            </div>
            <NavItems onNavigate={() => setDrawerOpen(false)} />
            <button onClick={doLogout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sunset hover:bg-white/5">
              <LogOut size={17} /> Logout
            </button>
          </aside>
        </div>
      )}

      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 lg:justify-end">
          <button onClick={() => setDrawerOpen(true)} className="flex items-center gap-2 text-navy lg:hidden" data-testid="admin-mobile-menu-btn">
            <Menu size={20} /> <span className="text-sm font-semibold">Menu</span>
          </button>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={doLogout} className="text-sm font-semibold text-sunset lg:hidden">Logout</button>
          </div>
        </header>
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
